/**
 * Schedule — the cross-Booking timeline (#303).
 *
 * Rows × days over a date window — three weeks by default, or whatever the
 * operator drags out on the calendar rail — with four lenses in one chrome: Jobs
 * (default — every Booking as a phase-coloured bar carrying its ex-GST
 * amount, with the window's total in the toolbar), Equipment (per-day
 * committed counts, quote holds shown as a lighter `+n`, graded against the
 * Company's own reserving phases), Vehicles and Crew (booking bars per fleet Vehicle /
 * per Contractor; an empty row means free).
 *
 * "Why is it blocked" is a click, not a cell: selecting a bar or an
 * equipment day opens a popover anchored to it — the booking's client,
 * value, trucks, gear and crew for a bar; the holding Bookings for an
 * equipment day — each with links into `/events/:id`.
 *
 * Lens, window, and row search live in the URL (`?lens=`, `?from=`, `?to=`,
 * `?q=`, committed with `replace`) so a filtered view is linkable and
 * survives Back.
 *
 * The Vehicles lane draws real hours (#305): a truck's bar is inset to the
 * time windows on its first and last day, so a morning job and an afternoon
 * job on the same Wednesday read as two bars rather than one blocked day.
 * Every other lane, and any bar whose windows carry no times, stays whole-day
 * — Assignments have no times at all, and an unknown window is a whole-day
 * commitment. The view degrades honestly rather than inventing hours.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  getSchedule,
  type ScheduleBooking,
  type ScheduleResponse,
  type ScheduleVehicleWindow,
} from "@/lib/schedule-api";
import {
  assignLanes,
  barInset,
  barTimeLabel,
  buildEquipmentRows,
  clipToWindow,
  lineCoversDay,
  nzToday,
  shiftISO,
  windowDays,
  type EquipmentRow,
  type Lane,
} from "@/lib/schedule-view";
import { PHASE_COLORS, PHASE_LABELS } from "@/lib/booking-phase";
import badge from "@/lib/status-badge.module.css";
import { formatDateRange } from "@/lib/format-utils";
import { formatNZDCompact } from "@shared/nz-money";
import { AdminPageHeader } from "@/components/ui/AdminPageHeader";
import { Banner } from "@/components/ui/Banner";
import { Button } from "@/components/ui/Button";
import { FolderTab, FolderTabBar } from "@/components/ui/FolderTabs";
import { MiniMonth } from "@/components/ui/MiniMonth";
import { Tooltip } from "@/components/ui/Tooltip";
import { useDismissable } from "@/lib/use-dismissable";
import forms from "../components/ui/field.module.css";
import { cx } from "@shared/cx";
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XIcon,
} from "@/components/ui/icons";
import page from "./page.module.css";
import styles from "./schedule.module.css";

/** The window's length when the URL names no end date. */
const DEFAULT_DAYS = 21;
/** Longest window the day grid stays readable at. */
const MAX_DAYS = 92;

const LENSES = ["jobs", "equipment", "vehicles", "crew"] as const;
type Lens = (typeof LENSES)[number];
const LENS_LABELS: Record<Lens, string> = {
  jobs: "Jobs",
  equipment: "Equipment",
  vehicles: "Vehicles",
  crew: "Crew",
};

/** What the label rail holds, per lens — the grid's one column heading. */
const RAIL_HEADINGS: Record<Lens, string> = {
  jobs: "Job",
  equipment: "Equipment",
  vehicles: "Vehicle",
  crew: "Crew",
};

/**
 * The Equipment rail's hint. The dual number means different things either
 * side of `quotes_hold_stock` (#314): where quotes reserve, both halves are
 * enforced by the picker; where they do not, the `+n` is planning
 * information the picker will ignore, and saying otherwise is the bug #344
 * fixed.
 */
function equipmentRailHint(quotesReserve: boolean): string {
  return quotesReserve
    ? "Units held each day, then quote-stage holds after the + — the counts the gear picker enforces."
    : "Units held each day, then quote-stage holds after the + — quotes don't reserve, so only the held count blocks a pick.";
}

function isLens(value: string): value is Lens {
  return (LENSES as readonly string[]).includes(value);
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * What the peek popover is showing, and the viewport rect of the cell it
 * hangs off. The rect is captured at click time because the popover is
 * positioned `fixed`: the timeline scrolls horizontally, and an
 * absolutely-positioned panel inside an `overflow-x: auto` ancestor is
 * clipped (the same invariant DESIGN-SYSTEM.md records for table dropdowns).
 */
type Peek = { anchor: DOMRect } & (
  | { kind: "booking"; bookingId: string }
  | { kind: "equipment-day"; equipmentId: string; day: string }
);

/** A Booking's display name, best-first. */
function bookingName(b: ScheduleBooking): string {
  return b.event_name || b.quote_number || b.client_name || "Untitled event";
}

/** The label rail's width, and the floor under one day column. */
const RAIL_PX = 176;
const DAY_PX = 40;

/**
 * How wide a row has to be for its sticky rail to stay pinned. Each row is a
 * block box, so without this it is only as wide as the scroll container while
 * its day columns overflow past the right edge — and a sticky cell only stays
 * pinned within its own containing block, so the rail scrolled away after
 * roughly one screen's worth of days. Sizing the row to its tracks makes the
 * containing block the whole timeline.
 */
function rowMinWidth(dayCount: number): string {
  return `${RAIL_PX + dayCount * DAY_PX}px`;
}

/** Shared column template: label rail + one column per day. */
function gridTemplate(dayCount: number): React.CSSProperties {
  return {
    gridTemplateColumns: `minmax(${RAIL_PX}px, 208px) repeat(${dayCount}, minmax(${DAY_PX}px, 1fr))`,
    minWidth: rowMinWidth(dayCount),
  };
}

/** Inclusive day count between two ISO dates. */
function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return DEFAULT_DAYS;
  return Math.round((b - a) / 86_400_000) + 1;
}

/** The sticky label rail cell every lens row starts with. */
function LabelCell({ children }: { children: React.ReactNode }) {
  return (
    <div className={styles.labelCell}>
      {children}
    </div>
  );
}

/**
 * A lens with no rows still lives inside the scrolling timeline, so its
 * message has to behave like one: sized to the whole grid, with the text
 * pinned to the rail instead of sliding away under the day header. `w-fit`
 * is load-bearing — a full-width block has nowhere to stick to.
 */
function EmptyRow({
  dayCount,
  children,
}: {
  dayCount: number;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: rowMinWidth(dayCount) }}>
      <p className={styles.emptyMessage}>
        {children}
      </p>
    </div>
  );
}

function dayLabel(iso: string): { weekday: string; day: string } {
  const d = new Date(`${iso}T00:00:00Z`);
  return {
    weekday: d.toLocaleDateString("en-NZ", {
      weekday: "narrow",
      timeZone: "UTC",
    }),
    day: d.toLocaleDateString("en-NZ", { day: "numeric", timeZone: "UTC" }),
  };
}

export default function SchedulePage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rawLens = searchParams.get("lens") ?? "";
  const lens: Lens = isLens(rawLens) ? rawLens : "jobs";
  const rawFrom = searchParams.get("from") ?? "";
  const from = ISO_DATE_RE.test(rawFrom) ? rawFrom : nzToday();
  // A window the operator dragged out carries its own end; without one the
  // window is the default three weeks.
  const rawTo = searchParams.get("to") ?? "";
  const customTo = ISO_DATE_RE.test(rawTo) && rawTo >= from ? rawTo : "";
  const dayCount = customTo
    ? Math.min(MAX_DAYS, daysBetween(from, customTo))
    : DEFAULT_DAYS;
  const to = shiftISO(from, dayCount - 1);
  const template = useMemo(() => gridTemplate(dayCount), [dayCount]);

  const [data, setData] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [peek, setPeek] = useState<Peek | null>(null);
  const [monthOpen, setMonthOpen] = useState(false);
  const monthRef = useRef<HTMLDivElement>(null);

  useDismissable({
    open: monthOpen,
    onDismiss: () => setMonthOpen(false),
    onEscape: () => setMonthOpen(false),
    pointer: [monthRef],
  });

  const setFilter = (patch: Record<string, string>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === "") next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
    setPeek(null);
  };

  /** Move the window by whole days, keeping whatever length it has. */
  const stepWindow = (by: number) =>
    setFilter({
      from: shiftISO(from, by),
      to: customTo ? shiftISO(to, by) : "",
    });

  /**
   * A click is a single day, not a jump: it drops whatever range was
   * selected and shows that day alone. A range is made by dragging, so a
   * stray click can never silently keep three weeks of window.
   */
  const jumpTo = (day: string) => setFilter({ from: day, to: day });

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    getSchedule({ from, to }, ac.signal)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        setError(
          err instanceof Error ? err.message : "Failed to load the schedule",
        );
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [from, to]);

  const days = useMemo(() => windowDays(from, dayCount), [from, dayCount]);
  const today = nzToday();

  /** One search box for every lens: it filters the rail, whatever the rail holds. */
  const query = (searchParams.get("q") ?? "").trim().toLowerCase();
  const matches = (...fields: (string | null)[]) =>
    query === "" || fields.some((f) => (f ?? "").toLowerCase().includes(query));

  const bookingsById = useMemo(() => {
    const map = new Map<string, ScheduleBooking>();
    for (const b of data?.bookings ?? []) map.set(b.id, b);
    return map;
  }, [data]);

  const equipmentRows = useMemo(
    () =>
      data
        ? buildEquipmentRows(
            data.equipment_lines,
            from,
            dayCount,
            data.quotes_hold_stock,
          )
        : [],
    [data, from, dayCount],
  );

  const shownBookings = (data?.bookings ?? []).filter((b) =>
    matches(b.event_name, b.client_name, b.quote_number),
  );
  const shownEquipment = equipmentRows.filter((row) => matches(row.name));
  const shownVehicles = (data?.vehicles ?? []).filter((v) =>
    matches(v.name, v.vehicle_type),
  );
  const shownContractors = (data?.contractors ?? []).filter((c) =>
    matches(c.name),
  );

  /** The Jobs lens toolbar total: every visible Booking's ex-GST amount. */
  const windowTotal = shownBookings.reduce(
    (sum, b) => sum + (b.quote_total ?? 0),
    0,
  );

  /** Booking bars per row key (vehicle id / contractor id), lane-packed. */
  const barsFor = (bookingIds: string[]): Lane<ScheduleBooking>[][] => {
    const bars: Lane<ScheduleBooking>[] = [];
    for (const id of bookingIds) {
      const b = bookingsById.get(id);
      if (!b) continue;
      const clip = clipToWindow(b.event_date, b.event_end_date, from, dayCount);
      if (!clip) continue;
      bars.push({ item: b, startIdx: clip.startIdx, span: clip.span });
    }
    bars.sort((a, z) => a.startIdx - z.startIdx);
    return assignLanes(bars);
  };

  /**
   * `windows` is set only on the Vehicles lane: the truck's time windows for
   * this bar, which inset it to the hours it is actually out. Every other
   * lane passes none and gets the whole-day block.
   */
  const bookingBar = (
    bar: Lane<ScheduleBooking>,
    lane: number,
    withAmount: boolean,
    windows?: ScheduleVehicleWindow[],
  ) => {
    const b = bar.item;
    const inset = windows
      ? barInset(windows, days, bar.startIdx, bar.span)
      : null;
    const timeLabel = windows
      ? barTimeLabel(windows, days, bar.startIdx, bar.span)
      : "";
    return (
      <button
        key={`${b.id}-${lane}`}
        type="button"
        onClick={(e) =>
          setPeek({
            kind: "booking",
            bookingId: b.id,
            anchor: e.currentTarget.getBoundingClientRect(),
          })
        }
        style={{
          gridColumn: `${bar.startIdx + 1} / span ${bar.span}`,
          gridRow: lane + 1,
          ...(inset && (inset.leftPct > 0 || inset.rightPct > 0)
            ? {
                marginLeft: `${inset.leftPct}%`,
                marginRight: `${inset.rightPct}%`,
              }
            : null),
        }}
        className={cx(styles.bar, PHASE_COLORS[b.phase])}
      >
        {bookingName(b)}
        {withAmount && b.quote_total !== null && (
          <span className={styles.barDetail}>
            {formatNZDCompact(b.quote_total)}
          </span>
        )}
        {timeLabel && <span className={styles.barDetail}>{timeLabel}</span>}
      </button>
    );
  };

  /**
   * One lens row of lane-packed booking bars behind a label rail. The bar
   * area is its own inner grid with the same day-column template as the
   * header, so its tracks align with the header's day cells (identical
   * minmax distribution — no subgrid, which the repo's browser floor
   * doesn't cover).
   */
  const barRow = (
    key: string,
    label: React.ReactNode,
    lanes: Lane<ScheduleBooking>[][],
    withAmount = false,
    windowsFor?: (bookingId: string) => ScheduleVehicleWindow[],
  ) => (
    <div
      key={key}
      className={styles.row}
      style={template}
      data-testid="schedule-row"
    >
      <LabelCell>{label}</LabelCell>
      <div
        className={styles.bars}
        style={{
          gridColumn: "2 / -1",
          gridTemplateColumns: `repeat(${dayCount}, minmax(${DAY_PX}px, 1fr))`,
        }}
      >
        {lanes.length === 0 ? (
          <div
            className={styles.emptyLane}
            style={{ gridColumn: `1 / span ${dayCount}` }}
          />
        ) : (
          lanes.flatMap((laneBars, lane) =>
            laneBars.map((bar) =>
              bookingBar(bar, lane, withAmount, windowsFor?.(bar.item.id)),
            ),
          )
        )}
      </div>
    </div>
  );

  const headerRow = (
    <div style={template} className={styles.headerRow}>
      <div className={styles.headerRail}>
        {lens === "equipment" ? (
          <Tooltip
            content={equipmentRailHint(data?.quotes_hold_stock ?? false)}
          >
            Equipment
          </Tooltip>
        ) : (
          RAIL_HEADINGS[lens]
        )}
      </div>
      {days.map((iso) => {
        const { weekday, day } = dayLabel(iso);
        const isToday = iso === today;
        return (
          <div
            key={iso}
            className={isToday ? styles.dayHeadToday : styles.dayHead}
          >
            <div className={styles.weekday}>{weekday}</div>
            <div>{day}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className={page.page}>
      <div className={page.content}>
        <AdminPageHeader title="Schedule" />

        {error && (
          <Banner
            variant="error"
            className={styles.banner}
            onDismiss={() => setError(null)}
          >
            {error}
          </Banner>
        )}

        {/*
          Below the rail's breakpoint there is no calendar to navigate with,
          so the navigator is its own row: Today, the week steppers, and the
          range as the heading they move, with the calendar behind it.
        */}
        <div className={styles.navigator}>
          <div className={styles.steppers}>
            <button
              type="button"
              aria-label="Previous week"
              onClick={() => stepWindow(-7)}
              className={styles.stepper}
            >
              <ChevronLeftIcon className={styles.stepperIcon} />
            </button>
            <button
              type="button"
              aria-label="Next week"
              onClick={() => stepWindow(7)}
              className={styles.stepper}
            >
              <ChevronRightIcon className={styles.stepperIcon} />
            </button>
          </div>
          <div ref={monthRef} className={styles.monthAnchor}>
            <button
              type="button"
              aria-expanded={monthOpen}
              aria-haspopup="dialog"
              onClick={() => setMonthOpen((o) => !o)}
              className={styles.monthTrigger}
            >
              {formatDateRange(from, to, "long")}
              <ChevronDownIcon className={styles.monthChevron} />
            </button>
            {monthOpen && (
              <div
                role="dialog"
                aria-label="Jump to date"
                className={styles.monthPopover}
              >
                <MiniMonth
                  start={from}
                  end={to}
                  today={today}
                  onPick={(day) => {
                    setMonthOpen(false);
                    jumpTo(day);
                  }}
                  onRange={(a, b) => {
                    setMonthOpen(false);
                    setFilter({ from: a, to: b });
                  }}
                />
              </div>
            )}
          </div>
        </div>

        <div className={styles.body}>
          {/*
            The navigator lives in the rail with the calendar it drives — the
            window it resolves to, then the month itself. The calendar's own
            chevrons step the month, so the toolbar's week steppers would be a
            second pair of arrows in the same column.
          */}
          <aside
            aria-label="Calendar"
            className={styles.rail}
          >
            <p className={styles.railRange}>
              {formatDateRange(from, to, "long")}
            </p>
            <div className={styles.railCalendar}>
              <MiniMonth
                start={from}
                end={to}
                today={today}
                onPick={jumpTo}
                onRange={(a, b) => setFilter({ from: a, to: b })}
              />
            </div>
          </aside>

          <div className={styles.timeline}>
            <FolderTabBar ariaLabel="Schedule lenses" wrap>
              {LENSES.map((key) => (
                <FolderTab
                  key={key}
                  selected={lens === key}
                  surface="page"
                  onSelect={() =>
                    setFilter({ lens: key === "jobs" ? "" : key })
                  }
                >
                  {LENS_LABELS[key]}
                </FolderTab>
              ))}
              {/*
            The search rides the tab bar's own row: it filters the rail of
            whichever lens is open, so it belongs to the tabs rather than
            sitting above them as a filter row of its own. Below `sm` there is
            no room beside four tabs, so it wraps onto its own full-width line
            rather than pushing the page into a horizontal scroll.
          */}
              <div
                role="presentation"
                className={styles.tabTools}
              >
                {/*
              The window's total is the one number the Jobs lens exists to
              answer, so it stays on a phone: it takes its own line above the
              search rather than being dropped at the width where the row
              runs out of room.
            */}
                {lens === "jobs" && shownBookings.length > 0 && (
                  <p className={styles.total}>
                    <Tooltip content="Every job shown in this window, at its quote total, excluding GST.">
                      Booked this window
                    </Tooltip>
                    :{" "}
                    <span className={styles.totalValue}>
                      {formatNZDCompact(windowTotal)}
                    </span>{" "}
                    <span className={styles.totalUnit}>excl. GST</span>
                  </p>
                )}
                <input
                  type="search"
                  aria-label="Search rows"
                  placeholder={`Search ${LENS_LABELS[lens].toLowerCase()}`}
                  value={searchParams.get("q") ?? ""}
                  onChange={(e) => setFilter({ q: e.target.value })}
                  className={cx(forms.inputCompact, styles.search)}
                />
              </div>
            </FolderTabBar>

            {loading ? (
              <p className={styles.loading}>Loading schedule…</p>
            ) : !data ? null : (
              <>
                {/*
              `overscroll-x-none` kills the rubber band at the ends of the
              scroll. Past `scrollLeft: 0` iOS clamps the scroll value but
              still translates the content right, and a sticky cell reads the
              clamped 0 — so the rail rode right with the days and left a white
              gutter behind it.
            */}
                <div className={styles.scroller}>
                  {headerRow}

                  {lens === "jobs" &&
                    (shownBookings.length === 0 ? (
                      <EmptyRow dayCount={dayCount}>
                        {query
                          ? "No jobs match this search."
                          : "Nothing scheduled in this window."}
                      </EmptyRow>
                    ) : (
                      shownBookings.map((b) => {
                        const clip = clipToWindow(
                          b.event_date,
                          b.event_end_date,
                          from,
                          dayCount,
                        );
                        if (!clip) return null;
                        return barRow(
                          b.id,
                          bookingName(b),
                          [
                            [
                              {
                                item: b,
                                startIdx: clip.startIdx,
                                span: clip.span,
                              },
                            ],
                          ],
                          true,
                        );
                      })
                    ))}

                  {lens === "equipment" &&
                    (shownEquipment.length === 0 ? (
                      <EmptyRow dayCount={dayCount}>
                        {query
                          ? "No equipment matches this search."
                          : "No gear is committed in this window."}
                      </EmptyRow>
                    ) : (
                      shownEquipment.map((row) => (
                        <div
                          key={row.equipmentId}
                          className={styles.row}
                          style={template}
                          data-testid="schedule-row"
                        >
                          <LabelCell>
                            {row.name}
                            {row.stock !== null && (
                              <span className={styles.stock}>
                                × {row.stock}
                              </span>
                            )}
                          </LabelCell>
                          {row.cells.map((cell, i) => {
                            const empty = cell.firm === 0 && cell.quote === 0;
                            const tone =
                              cell.severity === "over"
                                ? styles.cellOver
                                : cell.severity === "committed"
                                  ? styles.cellCommitted
                                  : undefined;
                            return empty ? (
                              <div
                                key={days[i]}
                                className={styles.cellEmpty}
                              />
                            ) : (
                              <button
                                key={days[i]}
                                type="button"
                                onClick={(e) =>
                                  setPeek({
                                    kind: "equipment-day",
                                    equipmentId: row.equipmentId,
                                    day: days[i],
                                    anchor:
                                      e.currentTarget.getBoundingClientRect(),
                                  })
                                }
                                aria-label={`${row.name} on ${days[i]}`}
                                className={cx(styles.cell, tone)}
                              >
                                {cell.firm}
                                {cell.quote > 0 && (
                                  // Quote-phase holds reserve too; lighter = "if
                                  // every quote closes", per the dual number.
                                  <span
                                    className={
                                      cell.severity === "none"
                                        ? styles.quoteAdvisory
                                        : styles.quoteCounted
                                    }
                                  >
                                    +{cell.quote}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      ))
                    ))}

                  {lens === "vehicles" &&
                    (shownVehicles.length === 0 ? (
                      <EmptyRow dayCount={dayCount}>
                        {query
                          ? "No vehicles match this search."
                          : "No vehicles in the fleet. Add them in Settings."}
                      </EmptyRow>
                    ) : (
                      shownVehicles.map((v) =>
                        barRow(
                          v.id,
                          v.name,
                          barsFor(
                            data.vehicle_bars
                              .filter((bar) => bar.vehicle_id === v.id)
                              .map((bar) => bar.booking_id),
                          ),
                          false,
                          // The one lane that draws hours (#305): the truck's own
                          // windows for each Booking on its row.
                          (bookingId) =>
                            data.vehicle_windows.filter(
                              (w) =>
                                w.vehicle_id === v.id &&
                                w.booking_id === bookingId,
                            ),
                        ),
                      )
                    ))}

                  {lens === "crew" &&
                    (shownContractors.length === 0 ? (
                      <EmptyRow dayCount={dayCount}>
                        {query
                          ? "No crew match this search."
                          : "No contractors yet."}
                      </EmptyRow>
                    ) : (
                      shownContractors.map((c) =>
                        barRow(
                          c.id,
                          c.name,
                          barsFor(
                            data.crew_bars
                              .filter((bar) => bar.contractor_id === c.id)
                              .map((bar) => bar.booking_id),
                          ),
                        ),
                      )
                    ))}
                </div>

                {peek && (
                  <PeekPanel
                    peek={peek}
                    data={data}
                    equipmentRows={equipmentRows}
                    bookingsById={bookingsById}
                    onClose={() => setPeek(null)}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const PEEK_WIDTH = 320;

/**
 * The popover shell: a card hanging off the cell that was clicked, the shape
 * every calendar uses for "what is this event". Positioned `fixed` from the
 * anchor's viewport rect so the timeline's horizontal scroll container cannot
 * clip it, flipping above the anchor when there is no room below, and clamped
 * to the viewport's edges. It closes on an outside pointer, on Escape, and on
 * a page scroll (which would otherwise leave it hanging over the wrong row).
 */
function PeekPopover({
  anchor,
  onClose,
  children,
}: {
  anchor: DOMRect;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useDismissable({
    open: true,
    onDismiss: onClose,
    onEscape: onClose,
    pointer: [ref],
  });

  useEffect(() => {
    const close = () => onClose();
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [onClose]);

  const left = Math.max(
    8,
    Math.min(anchor.left, window.innerWidth - PEEK_WIDTH - 8),
  );
  const below = window.innerHeight - anchor.bottom > 240;
  const position: React.CSSProperties = below
    ? { top: anchor.bottom + 6 }
    : { bottom: Math.max(8, window.innerHeight - anchor.top + 6) };

  return (
    <div
      ref={ref}
      style={{ position: "fixed", left, width: PEEK_WIDTH, ...position }}
      className={styles.peek}
    >
      {children}
    </div>
  );
}

/** An Assignment's response state, in the operator's words. */
const ASSIGNMENT_LABELS: Record<string, string> = {
  pending: "Not offered",
  notified: "Awaiting reply",
  accepted: "Accepted",
  declined: "Declined",
  cancelled: "Cancelled",
};

/** One label/value fact row in the booking popover. */
function PeekRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.peekRow}>
      <dt className={styles.peekLabel}>{label}</dt>
      <dd className={styles.peekValue}>
        {children}
      </dd>
    </div>
  );
}

/**
 * The "why is it blocked" drill-down: the grid answers *is it busy*, this
 * panel answers *who has it* (the Current RMS / Rentman split Morgan
 * described).
 */
function PeekPanel({
  peek,
  data,
  equipmentRows,
  bookingsById,
  onClose,
}: {
  peek: Peek;
  data: ScheduleResponse;
  equipmentRows: EquipmentRow[];
  bookingsById: Map<string, ScheduleBooking>;
  onClose: () => void;
}) {
  if (peek.kind === "booking") {
    const b = bookingsById.get(peek.bookingId);
    if (!b) return null;

    const contractorNames = new Map(
      data.contractors.map((c) => [c.id, c.name]),
    );
    const crew = data.crew_bars.filter((bar) => bar.booking_id === b.id);
    const vehicleNames = new Map(data.vehicles.map((v) => [v.id, v.name]));
    const vehicles = data.vehicle_bars
      .filter((bar) => bar.booking_id === b.id)
      .map((bar) => vehicleNames.get(bar.vehicle_id))
      .filter((name): name is string => !!name);
    const gearUnits = data.equipment_lines
      .filter((line) => line.booking_id === b.id)
      .reduce((sum, line) => sum + line.quantity, 0);

    return (
      <PeekPopover anchor={peek.anchor} onClose={onClose}>
        <section aria-label="Booking details">
          <div className={styles.peekHead}>
            <div className={styles.peekText}>
              <h2 className={styles.peekTitle}>
                {bookingName(b)}
              </h2>
              <p className={styles.peekSubtitle}>
                {formatDateRange(b.event_date, b.event_end_date, "long")}
              </p>
            </div>
            <button
              type="button"
              aria-label="Close"
              onClick={onClose}
              className={styles.peekClose}
            >
              <XIcon className={styles.peekCloseIcon} />
            </button>
          </div>
          <div className={styles.peekPhase}>
            <span
              className={cx(badge.badge, PHASE_COLORS[b.phase])}
            >
              {PHASE_LABELS[b.phase]}
            </span>
            <Button variant="link" size="sm" to={`/events/${b.id}`}>
              Open event
            </Button>
          </div>

          {/*
          The facts an operator asks the timeline for before opening the job:
          who it is for, what it is worth, and what it has already taken —
          trucks, gear, and the crew by name.
        */}
          <dl className={styles.peekFacts}>
            <PeekRow label="Client">{b.client_name || "—"}</PeekRow>
            <PeekRow label="Quote">{b.quote_number || "—"}</PeekRow>
            <PeekRow label="Value">
              {b.quote_total === null
                ? "—"
                : `${formatNZDCompact(b.quote_total)} excl. GST`}
            </PeekRow>
            <PeekRow label="Vehicles">
              {vehicles.length ? vehicles.join(", ") : "None"}
            </PeekRow>
            <PeekRow label="Gear">
              {gearUnits > 0 ? `${gearUnits} units` : "None"}
            </PeekRow>
          </dl>

          <div className={styles.peekCrew}>
            <p className={styles.peekCrewHeading}>Crew</p>
            {crew.length === 0 ? (
              <p className={styles.peekCrewEmpty}>Nobody assigned yet.</p>
            ) : (
              <ul className={styles.peekList}>
                {crew.map((bar, i) => (
                  <li
                    key={`${bar.contractor_id}-${i}`}
                    className={styles.peekRow}
                  >
                    <span className={styles.peekCrewName}>
                      {contractorNames.get(bar.contractor_id) ??
                        "Unknown contractor"}
                    </span>
                    <span className={styles.peekCrewStatus}>
                      {ASSIGNMENT_LABELS[bar.status] ?? bar.status}
                      {bar.vehicle_name && ` · ${bar.vehicle_name}`}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </PeekPopover>
    );
  }

  const row = equipmentRows.find((r) => r.equipmentId === peek.equipmentId);
  if (!row) return null;
  const holders = row.lines.filter((line) => lineCoversDay(line, peek.day));
  const { weekday, day } = dayLabel(peek.day);
  return (
    <PeekPopover anchor={peek.anchor} onClose={onClose}>
      <section aria-label="Equipment holds">
        <div className={styles.peekHead}>
          <h2 className={styles.peekTitle}>
            {row.name} — {weekday} {day}
          </h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className={styles.peekClose}
          >
            <XIcon className={styles.peekCloseIcon} />
          </button>
        </div>
        <ul className={styles.holdsList}>
          {holders.map((line, i) => {
            // A quote-stage line only holds anything where the Company sets
            // quotes_hold_stock (#314) — elsewhere the picker will hand the
            // same units to another Booking, so the panel must not call it held.
            const reserved =
              line.derived_phase !== "quote" || data.quotes_hold_stock;
            return (
              <li
                key={`${line.booking_id}-${i}`}
                className={styles.holdRow}
              >
                <div className={styles.holdText}>
                  <p className={styles.holdName}>
                    {line.event_name ||
                      line.quote_number ||
                      line.client_name ||
                      "Untitled event"}
                  </p>
                  <p className={styles.holdMeta}>
                    {line.quantity} {reserved ? "held" : "quoted"} ·{" "}
                    {formatDateRange(line.starts_on, line.ends_on, "short")}
                    {line.derived_phase === "quote" &&
                      (reserved ? " · quote hold" : " · not reserved")}
                  </p>
                </div>
                <Button
                  variant="link"
                  size="sm"
                  to={`/events/${line.booking_id}`}
                >
                  Open event
                </Button>
              </li>
            );
          })}
        </ul>
      </section>
    </PeekPopover>
  );
}
